import React, {
  createContext,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import { useAsyncEffect, useAsyncState, wrap } from "@hilma/tools";
import Axios from "axios";

import { UserProviderProps } from "../types";
import { useGetAccessToken } from "../hooks";

export const UserContext = createContext<unknown>(null);
export const GetUserContext = createContext<(() => Promise<void>) | null>(null);

const UserProvider = forwardRef(
  <U,>(
    {
      children,
      url,
      config,
      onFinish = () => null,
      onNoAccessToken = () => null,
      onError = () => null,
    }: UserProviderProps,
    ref?: React.Ref<U | null>,
  ) => {
    const [user, setUser] = useAsyncState<U | null>(null);

    const getAccessToken = useGetAccessToken();

    const accessToken = useMemo(getAccessToken, [getAccessToken]);

    const getUser = useCallback(async () => {
      try {
        if (!accessToken) {
          onNoAccessToken();
          return await setUser(null);
        }
        const { data: user } = await Axios(url, config);
        await setUser(user);
        onFinish(user);
        return user;
      } catch (error) {
        await setUser(null);
        onError(error);
        throw error;
      }
    }, [accessToken]);

    useAsyncEffect(async () => {
      try {
        await getUser();
      } catch (error) {
        // silence the error
      }
    }, [getUser]);

    useImperativeHandle(ref, () => user, [user]);

    return wrap(
      [UserContext.Provider, { value: user }],
      [GetUserContext.Provider, { value: getUser }],
    )(<>{children}</>) as React.ReactElement;
  },
);

export default UserProvider;
